import { useGetCountries } from 'src/shared/queries/useGetCountries';
import styles from './PermanentResidentDetails.module.scss';
import { useGetPriorityDates } from 'src/shared/queries/useGetPriorityDates';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { getFormattedDate, UserDateFormat } from 'src/utils/formatters/formatDate';
import { getCountryName, getReferenceDataDescription } from 'src/utils/services/common';
import { useGetReferenceData } from 'src/shared/queries/useGetReferenceData';
import { useGetDependents } from 'src/shared/queries/useGetDependents';
import { AsyncDataHandler } from '@ds';

const PermanentResidentDetails = () => {
  const { data: userInfo } = useGetUser();
  const { data: countries } = useGetCountries();
  const { data: nonCobaltAOSTypes } = useGetReferenceData('NON_COBALT_AOS');
  const { data: priorityDates, isLoading: isLoadingPriorityDates } = useGetPriorityDates(userInfo?.beneId);
  const { data: dependents } = useGetDependents(userInfo?.beneId ?? 0);
  const permanentResidentDetails =
    priorityDates && priorityDates.length > 0 ? priorityDates.filter((item) => !item.closePriorityDate) : [];

  return (
    <div className={styles['permanent-resident-details-section']}>
      <p className={`sm:px-0 md:p-2 mb-2`}>Permanent Resident Details</p>
      <AsyncDataHandler loader={{ width: '100%' }} isLoading={isLoadingPriorityDates}>
        {permanentResidentDetails.length > 0 ? (
          permanentResidentDetails.map((details, index) => (
            <div key={index} className={`px-4 py-5 ${styles['priority-dates']}`}>
              <h4>{details.priorityDateCategory}</h4>
              {details.fromPriorCounsel && <p className={`${styles['pr-medium']}`}>Prior Counsel</p>}
              {details.fromPreviousEmployer && <p className={`${styles['pr-medium']}`}>Previous Employer</p>}
              {details.priorityDate && (
                <div className={`pt-3`}>
                  <label className={`mb-0 ${styles['pr-medium']}`}>Priority Date</label>
                  <p className={`${styles['pr-large-bold']}`}>
                    {getFormattedDate(details.priorityDate as string, UserDateFormat.get())}
                  </p>
                </div>
              )}
              {details.laborCertification && (
                <div className={`pt-3`}>
                  <label className={`mb-0 ${styles['pr-medium']}`}>Labor Certification</label>
                  <p className={`${styles['pr-large-bold']}`}>
                    {details.laborCertification}&nbsp;-&nbsp;
                    {getFormattedDate(details.laborCertificationDate as string, UserDateFormat.get())}
                  </p>
                </div>
              )}
              {details.immigrantPetition && (
                <div className={`pt-3`}>
                  <label className={`mb-0 ${styles['pr-medium']}`}>Immigrant Petition</label>
                  <p className={`${styles['pr-large-bold']}`}>
                    {details.immigrantPetition}&nbsp;-&nbsp;
                    {getFormattedDate(details.immigrantPetitionDate as string, UserDateFormat.get())}
                  </p>
                </div>
              )}
              {details.chargeability && (
                <div className="pt-3">
                  <label className={`mb-0 ${styles['pr-large-medium']}`}>Cross-Chargeability</label>
                  <p className={`${styles['pr-medium']}`}>
                    {countries && getCountryName(countries, details.chargeability)}
                  </p>
                </div>
              )}
              {details.adjustmentOfStatus && (
                <div className="pt-3">
                  <label className={`mb-0 ${styles['pr-large-medium']}`}>Adjustment of Status</label>
                  <p className={`${styles['pr-medium']}`}>
                    {details.adjustmentOfStatus}&nbsp;-&nbsp;
                    {getFormattedDate(details.adjustmentOfStatusDate as string, UserDateFormat.get())}
                  </p>
                </div>
              )}
              {details.aosDependent && details.aosDependent.length > 0 && (
                <div className="pt-3">
                  <label className={`mb-0 ${styles['pr-large-medium']}`}>Dependents</label>
                  {details.aosDependent.map((dependent, index) => (
                    <p key={`dependent-${index}`} className={`${styles['pr-medium']}`}>
                      {dependents && dependents.find((dep) => dep.dependentId === dependent.dependentId)?.fullName}
                    </p>
                  ))}
                </div>
              )}
              {details.nonCobaltAos && (
                <div className="pt-3">
                  <label className={`mb-0 ${styles['pr-large-medium']}`}>Non-Cobalt AOS</label>
                  <p className={`${styles['pr-medium']}`}>
                    {nonCobaltAOSTypes && getReferenceDataDescription(nonCobaltAOSTypes, details.nonCobaltAos)}
                  </p>
                </div>
              )}
            </div>
          ))
        ) : (
          <p className={`sm:mx-0 md:m-2 p-4 ${styles['no-priority-dates']}`}>
            This section is to track priority date(s) associated with US Immigrant Visas. If you are not applying for an
            Immigrant Visa for the United States, you may ignore this section
          </p>
        )}
      </AsyncDataHandler>
    </div>
  );
};

export default PermanentResidentDetails;
